
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { getBilingualValue } from '@/utils/bilingual-helpers';
import i18n from '@/i18n/config';
import { notify } from '@/utils/notification';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Calendar, MapPin, Users, ArrowLeft, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { Helmet } from 'react-helmet-async';

const EventDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('events');
  const { user, activeRole } = useAuth();
  const lang = i18n.language;
  const [event, setEvent] = useState<any>(null);
  const [practitioner, setPractitioner] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [regCount, setRegCount] = useState(0);
  const [isRegistered, setIsRegistered] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [therapyNames, setTherapyNames] = useState<{ id: string; name_en: string; name_cn: string }[]>([]);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!id) return;
      setLoading(true);

      const { data, error } = await supabase
        .from('group_events')
        .select('*')
        .eq('id', id)
        .single();

      if (error || !data) {
        setLoading(false);
        return;
      }
      setEvent({ ...data, tags: data.tags || [] });

      // Fetch therapy tag names
      if (data.therapy_tag_ids && data.therapy_tag_ids.length > 0) {
        const { data: therapies } = await supabase
          .from('therapy_modalities')
          .select('id, name_en, name_cn')
          .in('id', data.therapy_tag_ids);
        if (therapies) setTherapyNames(therapies);
      }

      // Fetch practitioner
      const { data: prac } = await supabase
        .from('practitioners')
        .select('id, first_name, last_name, avatar_url, professional_title')
        .eq('id', data.practitioner_id)
        .single();
      setPractitioner(prac);

      // Fetch reg count
      const { count } = await supabase
        .from('group_event_registrations')
        .select('*', { count: 'exact', head: true })
        .eq('event_id', id)
        .in('status', ['registered', 'attended']);
      setRegCount(count || 0);

      // Check if current user is registered
      if (user?.id) {
        const { data: myReg } = await supabase
          .from('group_event_registrations')
          .select('id')
          .eq('event_id', id)
          .eq('client_id', user.id)
          .in('status', ['registered', 'attended'])
          .maybeSingle();
        setIsRegistered(!!myReg);
      }

      setLoading(false);
    };
    fetchEvent();
  }, [id, user?.id]);

  const handleRegister = async () => {
    if (!user?.id) {
      notify.error(t('loginRequired'));
      return;
    }
    if (activeRole !== 'client') {
      notify.error(t('clientOnly'));
      return;
    }
    setRegistering(true);
    try {
      const { error } = await supabase
        .from('group_event_registrations')
        .insert({
          event_id: id!,
          client_id: user.id,
          status: 'registered',
          payment_status: event.price > 0 ? 'pending' : 'paid',
        });
      if (error) throw error;
      setIsRegistered(true);
      setRegCount(prev => prev + 1);
      notify.success(t('registrationSuccess'));
    } catch (err: any) {
      console.error('Registration failed:', err);
      if (err.code === '23505') {
        notify.error(t('alreadyRegistered'));
      } else {
        notify.error(t('registrationFailed'));
      }
    } finally {
      setRegistering(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground">{t('eventNotFound')}</p>
        </div>
      </div>
    );
  }

  const title = getBilingualValue(lang, event.title_en, event.title_zh, event.title) || t('untitled');
  const description = getBilingualValue(lang, event.description_en, event.description_zh, event.description) || '';
  const spotsLeft = event.max_participants - regCount;
  const isFull = spotsLeft <= 0;
  const isPast = new Date(event.event_date) < new Date();

  // Local time conversion
  const getLocalTimeStr = () => {
    if (!event.timezone) return null;
    const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (userTz === event.timezone) return null;
    try {
      const eventDate = new Date(event.event_date);
      return format(eventDate, 'HH:mm') + ' (' + new Intl.DateTimeFormat('en', { timeZone: userTz, timeZoneName: 'short' }).formatToParts(eventDate).find(p => p.type === 'timeZoneName')?.value + ')';
    } catch { return null; }
  };
  const localTimeStr = getLocalTimeStr();

  return (
    <>
      <Helmet>
        <title>{title} | Resonow</title>
        <meta name="description" content={description.slice(0, 160)} />
      </Helmet>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-20 pb-12 px-4 max-w-4xl mx-auto w-full">
          <Link to="/events" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
            <ArrowLeft className="h-4 w-4" /> {t('backToEvents')}
          </Link>

          {event.cover_image_url && (
            <img src={event.cover_image_url} alt={title} className="w-full h-64 md:h-80 object-cover rounded-xl mb-8" />
          )}

          <div className="grid md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-6">
              <h1 className="text-3xl font-bold">{title}</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <Calendar className="h-4 w-4" />
                  {format(new Date(event.event_date), 'EEEE, MMMM d, yyyy · HH:mm')}
                  {event.timezone && (
                    <span className="text-xs opacity-70 ml-1">
                      ({(() => {
                        try {
                          const p = new Intl.DateTimeFormat('en', { timeZone: event.timezone, timeZoneName: 'short' })
                            .formatToParts(new Date()).find(p => p.type === 'timeZoneName');
                          return p?.value || event.timezone;
                        } catch { return event.timezone; }
                      })()})
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  {event.duration_minutes} {t('minutes')}
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" />
                  {event.location_type === 'virtual' ? `🌐 ${t('online')}` : `📍 ${event.location_city || event.location_details || t('inPerson')}`}
                </div>
              </div>

              {localTimeStr && (
                <p className="text-sm text-primary font-medium">
                  🕐 {t('localTime')}: {localTimeStr}
                </p>
              )}

              {/* Therapy Tags */}
              {therapyNames.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {therapyNames.map(th => (
                    <Badge key={th.id} variant="default" className="bg-blue-500 hover:bg-blue-600 text-white">
                      {lang === 'zh' ? th.name_cn : th.name_en}
                    </Badge>
                  ))}
                </div>
              )}

              {event.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {event.tags.map((tag: string) => (
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                  ))}
                </div>
              )}

              <div className="prose prose-sm max-w-none">
                <p className="whitespace-pre-wrap">{description}</p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="border rounded-xl p-6 space-y-4 bg-card shadow-sm sticky top-24">
                <div className="text-2xl font-bold">
                  {event.price > 0 ? `${event.currency} ${event.price}` : t('free')}
                </div>

                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Users className="h-4 w-4" />
                  <span>{regCount} / {event.max_participants} {t('registered')}</span>
                </div>

                {isPast ? (
                  <Badge variant="secondary" className="w-full justify-center py-2">{t('eventEnded')}</Badge>
                ) : isRegistered ? (
                  <Badge variant="default" className="w-full justify-center py-2">✓ {t('youAreRegistered')}</Badge>
                ) : isFull ? (
                  <Badge variant="destructive" className="w-full justify-center py-2">{t('full')}</Badge>
                ) : (
                  <Button className="w-full" size="lg" onClick={handleRegister} disabled={registering}>
                    {registering && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {event.price > 0 ? t('registerAndPay') : t('registerFree')}
                  </Button>
                )}

                {practitioner && (
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground mb-2">{t('hostedBy')}</p>
                    <div className="flex items-center gap-3">
                      {practitioner.avatar_url && (
                        <img src={practitioner.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                      )}
                      <div>
                        <p className="font-medium text-sm">{practitioner.first_name} {practitioner.last_name}</p>
                        {practitioner.professional_title && (
                          <p className="text-xs text-muted-foreground">{practitioner.professional_title}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default EventDetail;
